import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  FileText, 
  Printer, 
  Download, 
  Lock, 
  Calendar,
  User,
  MapPin,
  Clock,
  ChevronRight,
  Shield,
  Phone,
  Mail,
  Globe,
  Loader2,
  AlertTriangle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo CF AUDIO
const CFAudioLogo = () => (
  <div className="flex items-center gap-3">
    <img 
      src="https://customer-assets.emergentagent.com/job_sound-analysis-1/artifacts/g9zdtx14_cfaudio%20logo%202.png" 
      alt="CF AUDIO" 
      className="h-12 w-auto"
    />
  </div>
);

// Sections du rapport
const reportSections = [
  { id: 'resume', title: 'Résumé Exécutif', icon: FileText },
  { id: 'contexte', title: 'Contexte de l\'étude', icon: MapPin },
  { id: 'methodologie', title: 'Méthodologie', icon: Shield },
  { id: 'mesures', title: 'Mesures & Résultats', icon: FileText },
  { id: 'analyse', title: 'Analyse', icon: FileText },
  { id: 'conclusions', title: 'Conclusions', icon: FileText },
  { id: 'recommandations', title: 'Recommandations', icon: FileText },
];

const annexes = [
  { id: 'annexe-a', title: 'Annexe A - Fiches de mesure' },
  { id: 'annexe-b', title: 'Annexe B - Certificats d\'étalonnage' },
  { id: 'annexe-c', title: 'Annexe C - Références réglementaires' },
  { id: 'annexe-d', title: 'Annexe D - Glossaire technique' },
];

const ExpertiseReport = () => {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const [activeSection, setActiveSection] = useState('resume');
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch report data from API
  useEffect(() => {
    const fetchReport = async () => {
      if (!reportId) {
        setError('ID du rapport manquant');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const token = localStorage.getItem('tkale_token');
        
        const response = await axios.get(`${API}/acoustics/reports/${reportId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setReportData(response.data);
      } catch (err) {
        console.error('Error fetching report:', err);
        if (err.response?.status === 404) {
          setError('Rapport non trouvé');
        } else {
          setError('Erreur lors du chargement du rapport');
        }
        toast.error('Erreur lors du chargement du rapport');
      } finally {
        setIsLoading(false);
      }
    };

    fetchReport();
  }, [reportId]);

  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Non définie';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR');
    } catch {
      return dateStr;
    }
  };

  // Get section content based on report data
  const getSectionContent = () => {
    if (!reportData) return {};

    const { title, destinataire, site, periode, mesures, analyses, conclusions, recommandations, methodology } = reportData;

    return {
      resume: (
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-white">Résumé Exécutif</h3>
          <p className="text-slate-300 leading-relaxed">
            {reportData.description || `Ce rapport présente les résultats de l'étude acoustique "${title || 'Sans titre'}".`}
          </p>
          {mesures && mesures.length > 0 && (
            <div className="grid grid-cols-2 gap-4 mt-6">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-cyan-400">
                    {mesures[0]?.leq_global || 'N/A'} dB(A)
                  </div>
                  <div className="text-sm text-slate-400">Niveau moyen mesuré</div>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-amber-400">
                    {mesures.length} points
                  </div>
                  <div className="text-sm text-slate-400">Points de mesure</div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      ),
      contexte: (
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-white">Contexte de l'étude</h3>
          <p className="text-slate-300 leading-relaxed">
            {reportData.contexte || `L'étude a été commandée pour évaluer les niveaux sonores dans le cadre de ${title || 'cette mission acoustique'}.`}
          </p>
          
          {/* Destinataire info */}
          {destinataire && (
            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700 mt-4">
              <h4 className="font-medium text-white mb-3">Destinataire</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Société: </span>
                  <span className="text-slate-300">{destinataire.societe || destinataire.nom || 'Non renseigné'}</span>
                </div>
                <div>
                  <span className="text-slate-500">Contact: </span>
                  <span className="text-slate-300">{destinataire.nom || 'Non renseigné'}</span>
                </div>
                {destinataire.email && (
                  <div>
                    <span className="text-slate-500">Email: </span>
                    <span className="text-slate-300">{destinataire.email}</span>
                  </div>
                )}
                {destinataire.telephone && (
                  <div>
                    <span className="text-slate-500">Téléphone: </span>
                    <span className="text-slate-300">{destinataire.telephone}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Site info */}
          {site && (
            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700 mt-4">
              <h4 className="font-medium text-white mb-3">Site d'étude</h4>
              <div className="grid grid-cols-1 gap-2 text-sm">
                <div>
                  <span className="text-slate-500">Nom: </span>
                  <span className="text-slate-300">{site.nom || 'Non renseigné'}</span>
                </div>
                {site.adresse && (
                  <div>
                    <span className="text-slate-500">Adresse: </span>
                    <span className="text-slate-300">{site.adresse}</span>
                  </div>
                )}
                {site.type && (
                  <div>
                    <span className="text-slate-500">Type: </span>
                    <span className="text-slate-300">{site.type}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ),
      methodologie: (
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-white">Méthodologie</h3>
          <p className="text-slate-300 leading-relaxed">
            {methodology || `Les mesures acoustiques ont été réalisées selon les protocoles définis par la réglementation en vigueur.`}
          </p>
          
          {reportData.normes_applicables && reportData.normes_applicables.length > 0 && (
            <>
              <h4 className="font-medium text-white mt-4">Normes et réglementations applicables</h4>
              <ul className="list-disc list-inside text-slate-300 space-y-2">
                {reportData.normes_applicables.map((norme, idx) => (
                  <li key={idx}>{norme}</li>
                ))}
              </ul>
            </>
          )}

          {reportData.equipements && reportData.equipements.length > 0 && (
            <>
              <h4 className="font-medium text-white mt-4">Équipements utilisés</h4>
              <ul className="list-disc list-inside text-slate-300 space-y-2">
                {reportData.equipements.map((equip, idx) => (
                  <li key={idx}>{equip.marque} {equip.modele} - {equip.type}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      ),
      mesures: (
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-white">Mesures & Résultats</h3>
          {mesures && mesures.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-400">Point</th>
                    <th className="text-left py-3 px-4 text-slate-400">LAeq (dB)</th>
                    <th className="text-left py-3 px-4 text-slate-400">L50 (dB)</th>
                    <th className="text-left py-3 px-4 text-slate-400">Lmax (dB)</th>
                    <th className="text-left py-3 px-4 text-slate-400">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {mesures.map((mesure, idx) => (
                    <tr key={idx} className="border-b border-slate-800">
                      <td className="py-3 px-4 text-white">{mesure.point || `P${idx + 1}`}</td>
                      <td className="py-3 px-4 text-cyan-400">{mesure.leq_global || mesure.laeq || 'N/A'}</td>
                      <td className="py-3 px-4 text-slate-300">{mesure.l50 || 'N/A'}</td>
                      <td className="py-3 px-4 text-amber-400">{mesure.lmax || 'N/A'}</td>
                      <td className="py-3 px-4 text-slate-400">{formatDate(mesure.date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-slate-400">Aucune mesure enregistrée pour ce rapport.</p>
          )}
        </div>
      ),
      analyse: (
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-white">Analyse</h3>
          {analyses ? (
            <p className="text-slate-300 leading-relaxed">{analyses}</p>
          ) : (
            <p className="text-slate-400">L'analyse détaillée sera disponible après validation du rapport.</p>
          )}
          
          {reportData.conformite !== undefined && (
            <div className={`${reportData.conformite ? 'bg-emerald-900/30 border-emerald-700/50' : 'bg-amber-900/30 border-amber-700/50'} rounded-lg p-4 mt-4 border`}>
              <div className={`flex items-center gap-2 ${reportData.conformite ? 'text-emerald-400' : 'text-amber-400'} font-medium`}>
                <Shield className="w-5 h-5" />
                {reportData.conformite ? 'Conforme' : 'Point d\'attention'}
              </div>
              <p className={`${reportData.conformite ? 'text-emerald-200/80' : 'text-amber-200/80'} mt-2 text-sm`}>
                {reportData.commentaire_conformite || (reportData.conformite 
                  ? 'Les niveaux mesurés sont conformes aux seuils réglementaires.'
                  : 'Des mesures correctives peuvent être nécessaires.')}
              </p>
            </div>
          )}
        </div>
      ),
      conclusions: (
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-white">Conclusions</h3>
          {conclusions ? (
            <p className="text-slate-300 leading-relaxed">{conclusions}</p>
          ) : (
            <p className="text-slate-400">Les conclusions seront disponibles après finalisation du rapport.</p>
          )}
        </div>
      ),
      recommandations: (
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-white">Recommandations</h3>
          {recommandations && recommandations.length > 0 ? (
            <ul className="space-y-3">
              {recommandations.map((reco, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-cyan-400 text-sm font-bold">{idx + 1}</span>
                  </div>
                  <p className="text-slate-300">{typeof reco === 'string' ? reco : reco.description || reco.text}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-slate-400">Aucune recommandation spécifique pour ce rapport.</p>
          )}
        </div>
      ),
    };
  };

  const sectionContent = getSectionContent();

  const handlePrint = () => {
    window.print();
  };

  // PDF states
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Télécharger PDF - avec debug
  const handleDownloadPdf = () => {
    const token = localStorage.getItem('tkale_token');
    console.log('=== DEBUG PDF ===');
    console.log('Token:', token ? 'OK (' + token.substring(0,20) + '...)' : 'MANQUANT');
    console.log('Report ID:', reportId);
    
    if (!token) {
      toast.error('Session expirée');
      return;
    }
    
    const url = `${API}/acoustics/reports/${reportId}/pdf/download?token=${encodeURIComponent(token)}`;
    console.log('URL:', url);
    
    // Test direct
    console.log('Ouverture de l\'URL...');
    const newWindow = window.open(url, '_blank');
    console.log('Fenêtre ouverte:', newWindow ? 'OUI' : 'BLOQUÉE PAR POPUP BLOCKER');
    
    setShowPdfModal(false);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center" data-testid="expertise-report-loading">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-cyan-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Chargement du rapport...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center" data-testid="expertise-report-error">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
          <h2 className="text-xl text-white mb-2">{error}</h2>
          <p className="text-slate-400 mb-6">Impossible de charger ce rapport.</p>
          <Button 
            onClick={() => navigate('/acoustics/reports')}
            className="bg-cyan-600 hover:bg-cyan-500"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour aux rapports
          </Button>
        </div>
      </div>
    );
  }

  // No data state
  if (!reportData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center" data-testid="expertise-report-no-data">
        <div className="text-center">
          <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h2 className="text-xl text-white mb-2">Rapport non disponible</h2>
          <Button 
            onClick={() => navigate('/acoustics/reports')}
            className="bg-cyan-600 hover:bg-cyan-500 mt-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour aux rapports
          </Button>
        </div>
      </div>
    );
  }

  // Expert info (default values if not in report)
  const expertInfo = reportData.expert || {
    name: 'Claudio Fabiani',
    title: 'Designer Sonore',
    phone: '+33 6 12 17 16 75',
    email: 'cf@cfaudio.fr',
    website: 'cfaudio.fr'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="expertise-report">
      {/* Header */}
      <header className="border-b border-slate-700/50 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/acoustics/reports')}
                className="text-slate-400 hover:text-white"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Retour
              </Button>
              <CFAudioLogo />
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="border-red-500/50 text-red-400">
                <Lock className="w-3 h-3 mr-1" />
                {reportData.status || 'CONFIDENTIEL'}
              </Badge>
              <Button 
                variant="outline" 
                onClick={handlePrint}
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
                data-testid="print-button"
              >
                <Printer className="w-4 h-4 mr-2" />
                Imprimer
              </Button>
              {/* Bouton PDF - ouvre une modal avec liens directs */}
              <Button 
                onClick={() => setShowPdfModal(true)}
                className="bg-cyan-600 hover:bg-cyan-500"
                data-testid="pdf-button"
              >
                <Download className="w-4 h-4 mr-2" />
                PDF
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-cyan-900/20 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <Badge className="mb-4 bg-slate-800 text-slate-300">
              RAPPORT D'EXPERTISE
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {reportData.title || 'ACOUSTIQUE'}
            </h1>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              {reportData.type_etude || reportData.description || 'Étude acoustique'}
            </p>
          </motion.div>

          {/* Infos du rapport */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12"
          >
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 flex items-center gap-3">
                <User className="w-5 h-5 text-cyan-400" />
                <div>
                  <div className="text-xs text-slate-500">Destinataire</div>
                  <div className="text-white font-medium">
                    {reportData.destinataire?.societe || reportData.destinataire?.nom || 'Non renseigné'}
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 flex items-center gap-3">
                <Calendar className="w-5 h-5 text-cyan-400" />
                <div>
                  <div className="text-xs text-slate-500">Date du rapport</div>
                  <div className="text-white font-medium">{formatDate(reportData.created_at)}</div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 flex items-center gap-3">
                <FileText className="w-5 h-5 text-cyan-400" />
                <div>
                  <div className="text-xs text-slate-500">Référence</div>
                  <div className="text-white font-medium">{reportData.reference || reportData.report_id?.slice(0, 8) || 'N/A'}</div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 flex items-center gap-3">
                <Clock className="w-5 h-5 text-cyan-400" />
                <div>
                  <div className="text-xs text-slate-500">Période de mesures</div>
                  <div className="text-white font-medium">
                    {reportData.periode?.debut 
                      ? `${formatDate(reportData.periode.debut)} - ${formatDate(reportData.periode.fin)}`
                      : 'Non définie'}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Contenu principal */}
      <section className="max-w-7xl mx-auto px-6 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Navigation latérale */}
          <aside className="lg:col-span-1">
            <Card className="bg-slate-800/50 border-slate-700 sticky top-24">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm text-slate-400">SECTIONS</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <nav className="space-y-1 px-3 pb-3">
                  {reportSections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                        activeSection === section.id
                          ? 'bg-cyan-600/20 text-cyan-400'
                          : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
                      }`}
                    >
                      <section.icon className="w-4 h-4" />
                      <span className="text-sm">{section.title}</span>
                    </button>
                  ))}
                </nav>
                <div className="border-t border-slate-700 px-3 py-3">
                  <div className="text-xs text-slate-500 mb-2">ANNEXES</div>
                  {annexes.map((annexe) => (
                    <button
                      key={annexe.id}
                      onClick={() => setActiveSection(annexe.id)}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 rounded text-left text-sm transition-colors ${
                        activeSection === annexe.id
                          ? 'text-cyan-400'
                          : 'text-slate-500 hover:text-slate-300'
                      }`}
                    >
                      <ChevronRight className="w-3 h-3" />
                      {annexe.title}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </aside>

          {/* Contenu de la section */}
          <main className="lg:col-span-3">
            <Card className="bg-slate-800/30 border-slate-700">
              <CardContent className="p-8">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeSection}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    {sectionContent[activeSection] || (
                      <div className="text-slate-400 text-center py-12">
                        Contenu de {activeSection} à venir...
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </CardContent>
            </Card>
          </main>
        </div>
      </section>

      {/* Footer - Expert */}
      <footer className="border-t border-slate-700/50 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <div className="text-white font-semibold">{expertInfo.name}</div>
              <div className="text-slate-400 text-sm">{expertInfo.title}</div>
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-400">
              <a href={`tel:${expertInfo.phone}`} className="flex items-center gap-2 hover:text-cyan-400 transition-colors">
                <Phone className="w-4 h-4" />
                {expertInfo.phone}
              </a>
              <a href={`mailto:${expertInfo.email}`} className="flex items-center gap-2 hover:text-cyan-400 transition-colors">
                <Mail className="w-4 h-4" />
                {expertInfo.email}
              </a>
              <a href={`https://${expertInfo.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-cyan-400 transition-colors">
                <Globe className="w-4 h-4" />
                {expertInfo.website}
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Modal PDF */}
      <Dialog open={showPdfModal} onOpenChange={setShowPdfModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">Télécharger le PDF</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-slate-300 text-sm">
              La preview Emergent bloque les téléchargements. Copiez le lien et ouvrez-le dans un nouvel onglet :
            </p>
            
            <div className="flex gap-2">
              <input 
                type="text"
                readOnly
                value={`${API}/acoustics/reports/${reportId}/pdf/download?token=${localStorage.getItem('tkale_token') || ''}`}
                className="flex-1 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-xs text-slate-300"
              />
              <Button
                onClick={() => {
                  const url = `${API}/acoustics/reports/${reportId}/pdf/download?token=${localStorage.getItem('tkale_token') || ''}`;
                  navigator.clipboard.writeText(url);
                  toast.success('Lien copié ! Collez-le dans un nouvel onglet');
                }}
                className="bg-cyan-600 hover:bg-cyan-500"
              >
                Copier
              </Button>
            </div>
            
            <p className="text-slate-400 text-xs">
              💡 Ce problème n'existera pas sur l'app déployée (hors preview)
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpertiseReport;
